import Container from '@ui/container/container';
import OptionalComponent from '@ui/optional-component/optional-component';
import Text from '@/general-ui/text/text/text';
import Button from '@ui/buttons/button/button';
import { PASSCODE_LIST_TYPE, PasscodeList } from '@/types/registration.types';
import { ICONS } from '@ui/icons/types';
import Icon from '@ui/icons/icon';
import { useState } from 'react';
import { useAppSelector } from '@/store/hooks';
import { selectPasscodeLists } from '@/store/slices/passcode-lists/selectors';
import AddRegistrantToList from './registrants/modals/add-registrant-to-list-modal';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';

type SelectedRegistrant = {
  email: string;
  first_name?: string;
  last_name?: string;
};

type AudiencesTableSelectedMemberActionsProps = {
  selectedMembers?: number[] | string[];
  selectedRegistrants?: SelectedRegistrant[];
  passcodeList?: PasscodeList;
  isRegistration?: boolean;
  isMagicLinkRegistration?: boolean;
  isAllSelected?: boolean;
  selectAll?: () => void;
  clearSelectedMembers?: () => void;
  hideRegistrants?: () => void;
  removeSelectedPasscodeListMembers?: () => void;
  regenerateMagicLinks?: () => void;
  downloadMagicLinks?: () => void;
  downloadInfo?: () => void;
  hasNonDeletedSelected?: boolean;
  onReloadRegistrants?: () => void;
};

const AudiencesTableSelectedMemberActions = ({
  selectedMembers,
  selectedRegistrants = [],
  passcodeList,
  isRegistration = false,
  isMagicLinkRegistration = false,
  clearSelectedMembers,
  hideRegistrants,
  removeSelectedPasscodeListMembers,
  regenerateMagicLinks,
  downloadMagicLinks,
  downloadInfo,
  hasNonDeletedSelected = true,
  onReloadRegistrants,
}: AudiencesTableSelectedMemberActionsProps) => {
  const passcodeLists = useAppSelector(selectPasscodeLists);
  const [showAddListModal, setShowAddListModal] = useState(false);

  return (
    <>
      <Container
        className="flex jc-space-between ai-center padding-l-24 padding-r-18"
        width="100%"
        height="48px"
        background="var(--gray-800)"
        borderRadius="8px"
      >
        <Container className="flex ai-center gap-8">
          <Text fontSize="12px">
            {selectedMembers?.length} profile{selectedMembers && selectedMembers?.length > 1 ? 's' : ''} selected
          </Text>
        </Container>
        <Container className="flex fd-row jc-flex-end ai-center gap-8">
          <Container>
            <Button
              className="flex ai-center gap-4"
              onClick={clearSelectedMembers}
              backgroundColor="var(--gray-600)"
              hoverBackgroundColor="var(--gray-500)"
              height="32px"
            >
              <Icon name={ICONS.CLOSE} size={10} color="var(--gray-true-dark-100)" />
              <Text className="L2" color="var(--gray-true-dark-100)">
                Clear selection
              </Text>
            </Button>
          </Container>

          <OptionalComponent display={isRegistration && !passcodeList && hasNonDeletedSelected}>
            <Button
              className="flex ai-center gap-4"
              onClick={hideRegistrants}
              backgroundColor="var(--gray-600)"
              hoverBackgroundColor="var(--gray-500)"
              height="32px"
            >
              <Icon name={ICONS.TRASH} size={10} color="var(--gray-true-dark-100)" />
              <Text className="L2" color="var(--gray-true-dark-100)">
                Remove
              </Text>
            </Button>
          </OptionalComponent>
          <OptionalComponent display={(!!passcodeList || isMagicLinkRegistration) && !isRegistration}>
            <Button
              className="flex ai-center gap-4"
              onClick={removeSelectedPasscodeListMembers}
              backgroundColor="var(--gray-600)"
              hoverBackgroundColor="var(--gray-500)"
            >
              <Icon name={ICONS.TRASH} size={10} />
              <Text className="L2">Remove from List</Text>
            </Button>
          </OptionalComponent>

          <OptionalComponent display={!!passcodeList && passcodeList.type === PASSCODE_LIST_TYPE.MAGIC_LINKS}>
            <Button
              className="flex ai-center gap-4"
              onClick={regenerateMagicLinks}
              backgroundColor="var(--gray-600)"
              hoverBackgroundColor="var(--gray-500)"
            >
              <Icon name={ICONS.REFRESH} size={10} color="var(--gray-true-dark-100)" />
              <Text color="var(--gray-true-dark-100)">Regenerate Magic Links</Text>
            </Button>
          </OptionalComponent>

          <OptionalComponent
            display={
              isMagicLinkRegistration ||
              (!!passcodeList &&
                [PASSCODE_LIST_TYPE.MAGIC_LINKS, PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION].includes(
                  passcodeList.type
                ))
            }
          >
            <Button
              className="flex ai-center gap-4"
              onClick={downloadMagicLinks}
              backgroundColor="var(--gray-600)"
              hoverBackgroundColor="var(--gray-500)"
            >
              <Icon name={ICONS.DOWNLOAD} size={12} color="var(--gray-true-dark-100)" />
              <Text color="var(--gray-true-dark-100)" fontSize="12px">
                Download Magic Links
              </Text>
            </Button>
          </OptionalComponent>

          <OptionalComponent
            display={
              !!passcodeList &&
              [
                PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES,
                PASSCODE_LIST_TYPE.EMAILS_ONLY,
                PASSCODE_LIST_TYPE.PASSCODES_ONLY,
              ].includes(passcodeList.type)
            }
          >
            <Button
              className="flex ai-center gap-4"
              onClick={downloadInfo}
              backgroundColor="var(--gray-600)"
              hoverBackgroundColor="var(--gray-500)"
            >
              <Icon name={ICONS.DOWNLOAD} size={12} color="var(--gray-true-dark-100)" />
              <Text color="var(--gray-true-dark-100)" fontSize={'12px'}>
                Download info
              </Text>
            </Button>
          </OptionalComponent>
          <Button
            className="flex ai-center gap-4"
            height="32px"
            onClick={() => setShowAddListModal(true)}
            backgroundColor={getThemeColorCSSVariable(EColorVariablesV2.primary)}
          >
            <Icon
              name={ICONS.ADD}
              size={10}
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            />
            <Text
              className="L2"
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            >
              Add to list
            </Text>
          </Button>
        </Container>
      </Container>
      <AddRegistrantToList
        open={showAddListModal}
        setOpen={setShowAddListModal}
        passcodeLists={passcodeLists}
        registrants={selectedRegistrants}
        onSaveComplete={onReloadRegistrants}
      />
    </>
  );
};

export default AudiencesTableSelectedMemberActions;
